// src/components/reports/preview/WorkingTimeRecordPreview.js

import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatTime, formatDuration } from '../../../models/TimeEntryReportModel';

/**
 * Preview component for the Working Time Record report type.
 * One row per staff member per day: first clock-in, last clock-out,
 * presence, break and work time (same figures as Daily Summary).
 * @param {Object} props - Component props
 * @param {Array} props.data - Report data entries (one per staff per day)
 * @param {boolean} props.isPreview - Whether this is a limited preview
 * @param {number} props.previewLimit - Maximum number of rows to show in preview
 */
const WorkingTimeRecordPreview = ({ data, isPreview = true, previewLimit = 10 }) => {
  const { t } = useTranslation('reports');

  // Determine how many rows to display
  const displayData = isPreview ? data.slice(0, previewLimit) : data;

  // Calculate totals over ALL data, not only the visible preview rows
  const totalPresence = data.reduce((sum, entry) => sum + entry.presenceDuration, 0);
  const totalBreak = data.reduce((sum, entry) => sum + entry.breakDuration, 0);
  const totalWork = data.reduce((sum, entry) => sum + entry.workDuration, 0);

  return (
    <div className="report-preview working-time-record-preview">
      <div className="report-preview-scroll">
        <table className="report-table">
          <thead>
            <tr>
              <th className="date-col">{t('date')}</th>
              <th className="staff-col">{t('staff')}</th>
              <th className="time-col">{t('clockIn')}</th>
              <th className="time-col">{t('clockOut')}</th>
              <th className="duration-col">{t('presenceDuration')}</th>
              <th className="duration-col">{t('breakTime')}</th>
              <th className="duration-col">{t('workDuration')}</th>
            </tr>
          </thead>

          <tbody>
            {displayData.map((entry, index) => (
              <tr
                key={`${entry.staffId}-${entry.date.getTime()}-${index}`}
                className={index % 2 === 1 ? 'alternate-row' : ''}
              >
                <td>{formatDate(entry.date)}</td>
                <td>{entry.staffName}</td>
                <td>{formatTime(entry.clockIn)}</td>
                <td>{formatTime(entry.clockOut)}</td>
                <td>{formatDuration(entry.presenceDuration)}</td>
                <td>{formatDuration(entry.breakDuration)}</td>
                <td>{formatDuration(entry.workDuration)}</td>
              </tr>
            ))}

            {/* Show totals row */}
            <tr className="totals-row">
              <td colSpan={4}><strong>{t('total')}</strong></td>
              <td><strong>{formatDuration(totalPresence)}</strong></td>
              <td><strong>{formatDuration(totalBreak)}</strong></td>
              <td><strong>{formatDuration(totalWork)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Show message if there are more entries than shown in preview */}
      {isPreview && data.length > previewLimit && (
        <div className="preview-more-message">
          {t('andMoreEntries', { count: data.length - previewLimit })}
        </div>
      )}
    </div>
  );
};

export default WorkingTimeRecordPreview;